import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MatButtonToggleChange, MatDialog, MatSnackBar } from '@angular/material';
import { Router } from '@angular/router';
import { BaseComponent } from '@src/app/commons/BaseComponent';
import { Constants } from '@src/app/commons/Constants';
import { MapService } from '@src/app/commons/map.service';
import { PluginManagerService } from '@src/app/service/plugin-manager.service';
import { XsAwait } from '@src/app/xs/controllers/await.class';
import { XsApplicationEventsService } from '../../events/application-events.service';
import { UiManagerService } from '../ui-manager.service';

const Cesium = (<any>window).Cesium;

@Component({
  selector: 'fdm-split-viewer',
  templateUrl: './split-viewer.component.html',
  styleUrls: ['./split-viewer.component.scss'],
})
export class XsSplitViewerComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  canSplit: boolean = false;
  isSplitting: boolean = false;

  // reference to the slider HTML element
  splitSlider: HTMLElement = null;
  splitSliderContent: HTMLElement = null;

  // splitViewer is the extra transform we display in the slider
  splitViewer: HTMLElement = null;

  allLabels: any[] = [];

  // Whether the dropdowns should display 2D only, 3D only or all layers.
  dimensionSelection: string = 'all';

  leftSideId: string = null;
  leftSideTransform: any = null;

  rightSideId: string = null;
  rightSideTransform: any = null;

  // are we dragging the slider to and fro?
  dragging: boolean = false;

  // transforms of a imageLayer kind
  transforms: any[] = null;

  // index of image layers (and look at item.__idx for its index position in imageryLayers)
  LUT_t_idx: any = {};
  LUT_idx_t: any = {};

  // mouse and screen handler
  handler: any = null;

  constructor(
    // the three BaseComponent services
    public router: Router,
    public snackBar: MatSnackBar,
    public dialog: MatDialog,

    // others we need
    public uiManager: UiManagerService,
    public applicationEvents: XsApplicationEventsService,
    public pluginManager: PluginManagerService,
    public mapService: MapService
  ) {
    super(router, snackBar, dialog);
    if (this.debug) {
      console.log('%c SplitViewerCOMPONENT %c CONSTRUCT', Constants._red, Constants._pink, this, applicationEvents);
    }
  }

  init() {
    this.hideSplits();
  }

  end() {
    this.hideSplits();
    if (this.handler) {
      this.handler.destroy();
    }
  }

  ngOnChanges() {}

  fetchSplits() {
    if (!this.splitSlider) {
      this.splitSlider = document.getElementById('slider');
    }
    if (!this.splitSliderContent) {
      this.splitSliderContent = document.getElementById('sliderContent');
    }
    if (!this.splitViewer) {
      this.splitViewer = document.getElementById('splitViewer');
    }

    // both found?
    if (this.splitSlider && this.splitViewer) {
      return true;
    }
    return false;
  }

  hideSplits() {
    if (this.fetchSplits()) {
      // console.log('@@SPLITTER HIDE');
      this.splitSlider.hidden = true;
      this.splitSliderContent.hidden = true;
      this.splitViewer.hidden = true;
    }
  }

  showSplits() {
    if (this.fetchSplits()) {
      // console.log('@@SPLITTER SHOW');
      this.splitSlider.hidden = false;
      this.splitSliderContent.hidden = false;
      this.splitViewer.hidden = false;
    }
  }

  updateSplitView(scene: any = null) {
    // console.log('@@SPLITTER UPDATE AWAIT');
    let promise = new Promise((resolve) => {
      XsAwait.until(['mapService.viewer.scene', 'mapService.scene', 'mapService.currentProject'], this).then((_) => {
        // console.log('@@SPLITTER UPDATE GO');
        setTimeout(() => {
          this.readyUpdateSplitView(scene, resolve);
        }, 100);
      });
    });
    return promise;
  }

  readyUpdateSplitView(scene: any = null, resolve: any) {
    // const imageLayers = this.mapService.viewer.imageryLayers._layers;

    // console.log('@@SPLITTER !!', scene, scene ? 'SCENE' : 'ERROR : NO SCENE' );

    // incoming scene is full reset
    if (scene) {
      // quick enforcing of some orthos, seems like the featureDetector isn't fast enough sometimes
      this.mapService.sceneTransforms.forEach((t) => {
        // console.log('@@SPLITTER !!', t['ds_name']+' - '+t.id, scene['layerProps'][MapService.TOGGLE + t.id],  t, t['__detected'], t['__detected'] && t['__detected']['features'] && t['__detected']['features']['isImageLayer'] );
        if (!t['__detected']) {
          let e = this.mapService.helper.getMapObjectFor(t);
          if (e) {
            let entityType = e.constructor.name;
            let test = e['_imageryProvider'] ? true : false;
            if (entityType == 'ImageryProvider' || test) {
              t['__detected'] = { features: { isImageLayer: true } };
            }
          }
        }
      });

      this.transforms = this.mapService.sceneTransforms.filter((p) => {
        const isSceneLayer = scene['layerProps'][MapService.TOGGLE + p.id];
        const is2DLayer = p['__detected'] && p['__detected']['features'] && p['__detected']['features']['isImageLayer'];
        const is3DLayer = p['type_uid'] === 'three-d-tiles';
        const isFinished = p.status === 'finished';
        return isSceneLayer && isFinished && (is2DLayer || is3DLayer);
      });

      console.log(this.transforms.map((t) => [t.name, t.type_uid]));

      this.allLabels = this.transforms.map((l) => {
        return { label: l.ds_name + ' (' + l.id + ')', id: l.id };
      });

      this.leftSideTransform = null;
      this.rightSideTransform = null;

      this.canSplit = this.transforms.length && this.transforms.length > 1;

      if (this.canSplit) {
        console.log('@@SPLITTER CAN_SPLIT');

        // plot in the two defaults (two first found)
        this.transforms.forEach((layer) => {
          layer['canSplit'] = true;
          if (!this.leftSideTransform) {
            this.leftSideTransform = layer;
            this.leftSideId = layer.id;
          } else if (!this.rightSideTransform) {
            this.rightSideTransform = layer;
            this.rightSideId = layer.id;
          }
        });

        if (!this.handler) {
          console.log('@@SPLITTER NO_HANDLER, CREATING');
          this.handler = new Cesium.ScreenSpaceEventHandler(this.splitSlider);

          this.handler.setInputAction(() => (this.dragging = true), Cesium.ScreenSpaceEventType.LEFT_DOWN);
          this.handler.setInputAction(() => (this.dragging = true), Cesium.ScreenSpaceEventType.PINCH_START);

          this.handler.setInputAction(() => (this.dragging = false), Cesium.ScreenSpaceEventType.LEFT_UP);
          this.handler.setInputAction(() => (this.dragging = false), Cesium.ScreenSpaceEventType.PINCH_END);

          this.handler.setInputAction(this.move.bind(this), Cesium.ScreenSpaceEventType.MOUSE_MOVE);
          this.handler.setInputAction(this.move.bind(this), Cesium.ScreenSpaceEventType.PINCH_MOVE);
        } else {
          console.log('@@SPLITTER HAS_HANDLER, KEEPING IT');
        }
      } else {
        console.log('@@SPLITTER CONNOT_SPLIT');
        console.log('@@SPLITTER HAS_HANDLER, DELETING');
        this.destroyHandler();
      }
    }

    // console.log('@@SPLITTER UPDATE', this.canSplit, this.isSplitting, this.transforms, this.allLabels  );

    // from here we do soft reset; only update the two Transforms (and their properties)
    this.updateImageLayers();

    resolve(null);
  }

  destroyHandler() {
    if (this.handler) {
      this.handler.destroy();
    }
    this.handler = null;
  }

  updateImageLayers() {
    // console.log('@@SPLITTER LAYERS' );
    console.warn('callign update image layers');
    const imageLayers = this.mapService.viewer.imageryLayers._layers;
    const primitiveLayers = this.mapService.viewer.scene.primitives._primitives;
    const allLayers = [...imageLayers, ...primitiveLayers]
      .filter((l) => !!l.transform_id)
      .map((l) => ({ id: l.transform_id, layer: l, transform: this.transforms.find((t) => t.id === l.transform_id) }));

    console.log(allLayers);

    if (this.isSplitting && allLayers && allLayers.length >= 2) {
      const leftLayer = allLayers.find((l) => l.id === this.leftSideId);
      const rightLayer = allLayers.find((l) => l.id === this.rightSideId);
      console.log('left id', this.leftSideId, 'left layer', leftLayer, 'right id', this.rightSideId, 'right layer', rightLayer);

      allLayers.forEach((layer) => {
        if (layer === leftLayer) layer.layer.splitDirection = Cesium.SplitDirection.LEFT;
        else if (layer === rightLayer) layer.layer.splitDirection = Cesium.SplitDirection.RIGHT;
        else layer.layer.splitDirection = Cesium.SplitDirection.NONE;
      });
    }
  }

  updateLayerChoices(selection: MatButtonToggleChange) {
    const is2DLayer = (p: any) => p['__detected'] && p['__detected']['features'] && p['__detected']['features']['isImageLayer'];
    const is3DLayer = (p: any) => p['type_uid'] === 'three-d-tiles';
    const getLayerChoice = (l: any) => ({ label: l.ds_name + ' (' + l.id + ')', id: l.id });

    if (this.dimensionSelection === '2d') {
      this.allLabels = this.transforms.filter((t) => is2DLayer(t)).map((l) => getLayerChoice(l));
    } else if (this.dimensionSelection === '3d') {
      this.allLabels = this.transforms.filter((t) => is3DLayer(t)).map((l) => getLayerChoice(l));
    } else {
      this.allLabels = this.transforms.map((l) => getLayerChoice(l));
    }
  }

  updateLeftSide(id: number) {
    // console.log('@@SPLITTER UPDATE_LEFT', id );
    let find = this.transforms.find((t) => t.id == id);
    if (find) {
      this.leftSideTransform = find;
      this.updateSplitView().then((_) => {
        this.leftSideId = this.leftSideTransform.id;
      });
    }
  }

  updateRightSide(id: number) {
    // console.log('@@SPLITTER UPDATE_RIGHT', id );
    let find = this.transforms.find((t) => t.id == id);
    if (find) {
      this.rightSideTransform = find;
      this.updateSplitView().then((_) => {
        this.rightSideId = this.rightSideTransform.id;
      });
    }
  }

  start(scene: any = null) {
    // console.log('@@SPLITTER START', scene );

    if (scene) {
      this.isSplitting = true;
      this.updateSplitView(scene).then((_) => {
        if (this.canSplit) {
          this.leftSideId = this.leftSideTransform.id;
          this.rightSideId = this.rightSideTransform.id;
          // delay so the template can draw itself before we try to attach events
          setTimeout(() => {
            this.showSplits();
            this.move();
            // this.mapService.helper.sendCameraTo(this.leftSideTransform);
          }, 100);
        } else {
          this.isSplitting = false;
        }
      });
    }
  }

  stop() {
    // console.log('@@SPLITTER STOP');

    this.leftSideTransform = null;
    this.rightSideTransform = null;
    this.updateImageLayers();

    this.mapService.viewer.scene.splitPosition = 0;
    this.isSplitting = false;

    this.hideSplits();

    this.destroyHandler();
  }

  move(movement?: { endPosition: { x: number } }) {
    if (!this.dragging && movement) return;

    const relativeOffset = movement ? movement.endPosition.x : 0.5;
    const splitPosition = (this.splitSlider.offsetLeft + relativeOffset) / this.splitSlider.parentElement.offsetWidth;
    const relativePosLeft = 100.0 * splitPosition;

    // Ensures the slider and content can't be moved off the screen.
    let adjustedPosLeft = relativePosLeft;
    if (relativePosLeft < 0) adjustedPosLeft = 0;
    else if (relativePosLeft > 100) adjustedPosLeft = 100;

    // Sets how far left the slider and selection dropdowns are positioned (as a percentage of parent element width).
    const percentageLeft = `${adjustedPosLeft}%`;
    this.splitSlider.style.left = percentageLeft;
    this.splitSliderContent.style.left = percentageLeft;

    this.mapService.viewer.scene.splitPosition = splitPosition;
  }
}
